<template>
  <!-- Proxy login: username + password, or an IP whitelist (login window on My Proxies and the purchase window) -->
  <div class="plf">
    <div class="plf-seg" role="tablist">
      <button type="button" role="tab" :aria-selected="model.mode === 'login'" :class="{ on: model.mode === 'login' }" @click="model = { ...model, mode: 'login' }">
        <v-icon icon="mdi-key-variant" size="15"></v-icon> Username & password</button>
      <button type="button" role="tab" :aria-selected="model.mode === 'ips'" :class="{ on: model.mode === 'ips' }" @click="model = { ...model, mode: 'ips' }">
        <v-icon icon="mdi-web" size="15"></v-icon> IP whitelist</button>
    </div>

    <template v-if="model.mode === 'login'">
      <div class="plf-grid">
        <label class="plf-field">
          <span class="plf-lbl">Username</span>
          <input :value="model.user" @input="model = { ...model, user: $event.target.value.trim() }" class="plf-in" autocomplete="off" spellcheck="false" maxlength="32" />
          <small :class="hintClass(model.user)"><v-icon v-if="nameOk(model.user, minLength)" icon="mdi-check" size="12"></v-icon>{{ hintText }}</small>
        </label>
        <label class="plf-field">
          <span class="plf-lbl">Password</span>
          <span class="plf-wrap">
            <input :value="model.pass" @input="model = { ...model, pass: $event.target.value.trim() }" :type="show ? 'text' : 'password'" class="plf-in" autocomplete="new-password" spellcheck="false" maxlength="32" />
            <button type="button" class="plf-eye" :aria-label="show ? 'Hide password' : 'Show password'" @click="show = !show"><v-icon :icon="show ? 'mdi-eye-off-outline' : 'mdi-eye-outline'" size="17"></v-icon></button>
          </span>
          <small :class="hintClass(model.pass)"><v-icon v-if="nameOk(model.pass, minLength)" icon="mdi-check" size="12"></v-icon>{{ hintText }}</small>
        </label>
      </div>
      <div class="plf-links">
        <button type="button" @click="model = { ...model, ...randomLogin() }"><v-icon icon="mdi-refresh" size="14"></v-icon> Make a random login</button>
        <button type="button" @click="copyLine"><v-icon icon="mdi-content-copy" size="14"></v-icon> Copy</button>
      </div>
      <div>
        <span class="plf-lbl">{{ host && port ? 'Your proxy will be' : 'Your login' }}</span>
        <div class="plf-prev" translate="no">{{ line }}</div>
      </div>
    </template>

    <template v-else>
      <label class="plf-field">
        <span class="plf-lbl">IP addresses (one per line)</span>
        <textarea :value="model.ips" @input="model = { ...model, ips: $event.target.value }" rows="3" class="plf-in plf-area" spellcheck="false" placeholder="1.1.1.1&#10;2.2.2.2"></textarea>
        <small v-if="badIps.length" class="bad">Not an IPv4 address: {{ badIps.join(', ') }}</small>
        <small v-else-if="ipList.length" class="ok"><v-icon icon="mdi-check" size="12"></v-icon>{{ ipList.length }} address(es) OK</small>
        <small v-else>Only these addresses can use the proxy, no login needed.</small>
      </label>
      <div class="plf-links">
        <button type="button" :disabled="findingIp" @click="addMyIp"><v-icon icon="mdi-plus" size="14"></v-icon> Add my current IP</button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import copy from 'clipboard-copy';
import { fetchWrapper } from '@/helpers';
import { useAlertStore } from '@/stores';
import { nameOk, ipOk, splitIps, randomLogin } from '@/helpers/proxyLogin';

const props = defineProps({ modelValue: { type: Object, required: true }, minLength: { type: Number, default: 1 }, host: String, port: [String, Number] });
const emit = defineEmits(['update:modelValue']);
// { mode: 'login' | 'ips', user, pass, ips } (v-model)
const model = computed({ get: () => props.modelValue, set: (v) => emit('update:modelValue', v) });
const show = ref(false);
const findingIp = ref(false);

const hintText = computed(() => props.minLength > 1 ? `${props.minLength}+ letters or numbers` : 'Letters and numbers only');
const hintClass = (v) => (!v ? '' : nameOk(v, props.minLength) ? 'ok' : 'bad');
const line = computed(() => {
  const login = `${model.value.user || '…'}:${model.value.pass || '…'}`;
  return props.host && props.port ? `${props.host}:${props.port}:${login}` : login;
});
const ipList = computed(() => splitIps(model.value.ips));
const badIps = computed(() => ipList.value.filter((ip) => !ipOk(ip)));

const copyLine = () => { copy(line.value); useAlertStore().success('Copied'); };
const addMyIp = async () => {
  findingIp.value = true;
  try {
    const r = await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/proxy/my-ip`);
    const ip = String((r && r.ip) || '');
    if (!ipOk(ip)) useAlertStore().error(`Your connection uses ${ip}: the whitelist takes IPv4 addresses only.`);
    else if (!ipList.value.includes(ip)) model.value = { ...model.value, ips: [...ipList.value, ip].join('\n') };
  } catch (e) { useAlertStore().error(e); }
  findingIp.value = false;
};
</script>

<style scoped>
.plf { display: flex; flex-direction: column; gap: 12px; }
.plf-seg { display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #dee2e6; border-radius: 8px; overflow: hidden; }
.plf-seg button { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 7px 10px; font-size: 13px; color: inherit; background: transparent; }
.plf-seg button.on { background: #6366f1; color: #ffffff; }
.plf-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.plf-field { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.plf-lbl { display: block; font-size: 12px; color: #6b7280; margin-bottom: 2px; }
.plf-in { width: 100%; height: 36px; padding: 0 10px; border: 1px solid #ced4da; border-radius: 8px; background: transparent; color: inherit; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 13px; }
.plf-in:focus { outline: none; border-color: #6366f1; }
.plf-area { height: auto; padding: 8px 10px; resize: vertical; }
.plf-wrap { position: relative; display: block; }
.plf-wrap .plf-in { padding-right: 34px; }
.plf-eye { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); color: #6b7280; background: transparent; }
.plf-field small { font-size: 11px; color: #6b7280; display: inline-flex; align-items: center; gap: 2px; }
.plf-field small.ok { color: #22c55e; }
.plf-field small.bad { color: #ef4444; }
.plf-links { display: flex; justify-content: space-between; gap: 10px; }
.plf-links button { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: #6366f1; background: transparent; }
.plf-links button:disabled { opacity: 0.5; }
.plf-prev { border: 1px dashed #ced4da; border-radius: 8px; padding: 7px 10px; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 12px; overflow-wrap: anywhere; }
</style>

<style>
/* dark theme: the site's own colours (unscoped so body.dark reaches it) */
body.dark .plf-seg, body.dark .plf-in, body.dark .plf-prev { border-color: #45445e; }
body.dark .plf-lbl, body.dark .plf-eye, body.dark .plf-field small:not(.ok):not(.bad) { color: #bcbbc7; }
body.dark .plf-links button { color: #a5a3f7; }
</style>
